import React, { ReactElement, useState, useEffect } from 'react';
import { KView, KText, PageContext, KImage } from '@/component/RtlLayout';
import { StyleSheet, Text, View } from 'react-native';
import { convertStyles } from '@kibt/krn-utils';
import { $k18n } from '@/common/i18n';
import Toast, { useToast } from '@/component/Toast/Toast';
import { productSales } from '@/page/detail/views/content/18nText';
import iconTips from '@/assets/icon_detail/icon_tips.png';
import { isSnack } from '@/common/device-info';
import { useShowFlash } from '@/hooks/abTest/useShowFlash';
import SrcStyles from './index.styl';
import { ViewVisible } from '@/component/ViewVisible/ViewVisible';
import { logClickSpecs, logShowSpecs } from '@/page/detail/views/logger2.0';
import { useEntranceType } from '@/page/detail/views/content/const';
import CropImage from '@/business-components/CropImage';
import { showFlashActivity } from '@/page/detail/views/content/utils';
import FlashProgress from '../FlashProgress';
import { k18nPro } from '@/component/k18n/K18n';
import { FlatList } from '@/component/GestureHandler';
import { useQuery } from '@/hooks/useQuery';
import { ActivityType } from '../BigMarketingBanner/CaculateBlock';
import { useDetailPageStore } from '@/page/detail/store';
import isEmpty from 'lodash/isEmpty';
import { useSubsidy } from '../../hooks/useSubsidy';
import { KTouchableOpacity, KTouchableWithoutFeedback } from '@/component/RtlLayout/Touchable';
import { Spec, WarmUpPrice } from '../../../api';
import { scaleW } from '@/common/style';
import CouponEntryLineV2 from '../CouponEntryLineV2';
import { ProductRank } from '../ProductRank';

const Styles = convertStyles(SrcStyles);

export type PriceObj = {
    price?: number | string;
    displayPrice?: string;
    ifAllSame?: boolean;
};

const styles = StyleSheet.create({"up":{"transform":[{"rotate":"180deg"}]},"inventory":{"flexGrow":1,"display":"flex"}});

/**
 * 商详价格区域
 */
function PriceLine(): ReactElement {
    const { delivery, currentSku, immediateAvailableQuantity, displayCommissionRate, specImageIndex, bigMarketingBanner, pageRawData } =
        useDetailPageStore(s => ({
            delivery: s.delivery,
            currentSku: s.currentSku,
            immediateAvailableQuantity: s.immediateAvailableQuantity,
            displayCommissionRate: s.displayCommissionRate,
            specImageIndex: s.specImageIndex,
            bigMarketingBanner: s.bigMarketingBanner,
            pageRawData: s.pageRawData,
        }));

    const isNotAllImg = !(pageRawData?.specs?.[0]?.itemSpecValues?.[0]?.imageUrl && pageRawData?.specs?.[0]?.itemSpecValues?.[0]?.imageUrl !== '');

    const setStoreState = useDetailPageStore().setState;
    const getStoreState = useDetailPageStore().getState;
    const { showToast, toastNode } = useToast();
    const itemId = useQuery('itemId');

    const [titleInfo, setTitleInfo] = useState({
        numLines: null,
        maxHeight: 0,
        showOpenDetail: false,
        isFlat: false,
    });

    // showAbFlash标识是否命中商详页的ab实验。命中实验才展示秒杀腰带、秒杀进度条；没有命中ab实验，如果有秒杀活动，只展示秒杀价
    const { showFlash: showAbFlash } = useShowFlash();
    // 是否展示腰封：有活动id，并且活动状态是秒杀中才展示秒杀氛围模块（秒杀腰带和秒杀价）|| 大促期间
    const [showFlash, setShowFlash] = useState(
        pageRawData?.activityInfo?.activityId &&
            [2001, 2002, 2007].includes(pageRawData?.activityInfo?.marketingActivityType) &&
            showFlashActivity(pageRawData?.activityInfo?.status) &&
            pageRawData?.activityInfo?.saleRate <= 100
    );
    // 秒杀进度在50-100之间，展示秒杀进度条
    const [showFlashProgress, setShowFlashProgress] = useState(
        showFlash && pageRawData?.activityInfo?.saleRate >= 50 && pageRawData?.activityInfo?.saleRate <= 100
    );

    useEffect(() => {
        setShowFlash(
            pageRawData?.activityInfo?.activityId &&
                [2001, 2002, 2007].includes(pageRawData?.activityInfo?.marketingActivityType) &&
                showFlashActivity(pageRawData?.activityInfo?.status) &&
                pageRawData?.activityInfo?.saleRate <= 100
        );
        setShowFlashProgress(pageRawData?.activityInfo?.saleRate >= 50 && pageRawData?.activityInfo?.saleRate <= 100);
    }, [pageRawData?.activityInfo?.activityId, pageRawData?.activityInfo?.status, pageRawData?.activityInfo?.saleRate]);

    const specs = pageRawData.specs
        ? (pageRawData.specs.map((item: Spec) => {
              if (item) {
                  item.specName = item.specName;
                  return item;
              }
          }) as Spec[])
        : ([] as Spec[]);

    const isAfterUsingCoupon = pageRawData.salePriceInclude?.includes('COUPON');

    // 每个主规格下的sku的价格集合，eg: specOneStatus[0].salePrice 就是第一个主规格下的所有sku的所有salePrice，具体逻辑可以看setSpecOneStatus
    const [specOneStatus, setSpecOneStatus] = useState(
        [] as {
            salePrice: {
                price: number;
                displayPrice: string;
                marketPrice: string;
            }[]; // 最终售价
            saleMarketPrice: {
                price: number;
                displayPrice: string;
                marketPrice: string;
            }[]; // 活动划线价
            activityPrice: {
                price: number;
                displayPrice: string;
                marketPrice: string;
            }[];
            skuPrice: {
                price: number;
                displayPrice: string;
                marketPrice: string;
            }[];
            skuMarketPrice: [];
            installments: any[];
            displaySkuMarketPrice: []; // 划线价
            availableQuantity: false;
            skuWarmUpPrice: WarmUpPrice[];
        }[]
    );

    const initSpecOneStatus = () => {
        const arr = [];
        // 把skus数据按第一层spec划分出来
        for (const itemSpecValues of specs[0].itemSpecValues) {
            const res: any = [];
            for (const sku of pageRawData.skus) {
                if (sku.skuSpecs[0].specValueId === itemSpecValues.specValueId) {
                    // 获取sku的分期价格
                    const installment = pageRawData.skuInstallments?.find((_installment: { skuId: any }) => _installment.skuId === sku.skuId) || {};
                    // 获取sku的预热价格
                    const skuWarmUpPrice =
                        pageRawData.bigSaleInfoVO?.skuWarmUpPrice?.[sku.skuId] ||
                        ({
                            warmUpPrice: Number.MAX_SAFE_INTEGER,
                            displayWarmUpPrice: '',
                        } as WarmUpPrice);
                    const skuWithInstallment = { ...sku, installment, skuWarmUpPrice };
                    res.push(skuWithInstallment);
                }
            }
            const status: any = {
                salePrice: [], // 最终售价
                saleMarketPrice: [], // 活动划线价
                activityPrice: [],
                skuPrice: [],
                skuMarketPrice: [],
                installments: [],
                displaySkuMarketPrice: [], // 划线价
                availableQuantity: false,
                skuWarmUpPrice: [] as WarmUpPrice[],
            };
            res?.forEach(
                (item: {
                    skuPrice: any;
                    displaySkuPrice: any;
                    skuMarketPrice: any;
                    displaySkuMarketPrice: any;
                    displayMarketPrice: any;
                    salePrice: any;
                    displaySalePrice: any;
                    activityPrice: any;
                    displayActivityPrice: any;
                    installment: any;
                    skuWarmUpPrice: WarmUpPrice;
                }) => {
                    if (!item) return;
                    // 添加所有价格和划线价
                    status.skuPrice.push({
                        price: item.skuPrice,
                        displayPrice: item.displaySkuPrice,
                        marketPrice: item.skuMarketPrice ? item.displaySkuMarketPrice : item.displayMarketPrice,
                    });
                    // 添加券后价格、在活动（秒杀）价格的基础上，再减去优惠券的价格
                    status.salePrice.push({
                        price: item.salePrice,
                        displayPrice: item.displaySalePrice,
                        marketPrice: item.skuMarketPrice ? item.displaySkuMarketPrice : item.displayMarketPrice,
                    });
                    // 添加活动价格
                    status.activityPrice.push({
                        price: item.activityPrice,
                        displayPrice: item.displayActivityPrice,
                        marketPrice: item.skuMarketPrice ? item.displaySkuMarketPrice : item.displayMarketPrice,
                    });
                    status.saleMarketPrice.push({
                        price: item.skuMarketPrice ? item.skuMarketPrice : item.skuPrice, // 有活动划线价展示划线价，无则展示商品原价
                        displayPrice: item.skuMarketPrice ? item.displaySkuMarketPrice : item.displaySkuPrice,
                    });
                    status.installments.push(item.installment);
                    status.skuMarketPrice.push(item.skuMarketPrice);
                    status.displaySkuMarketPrice.push(item.displaySkuMarketPrice);
                    status.skuWarmUpPrice.push(item.skuWarmUpPrice);
                }
            );

            // 添加库存数据
            let availableQuantityRes = 0;
            for (const item of res) {
                if (item.availableQuantity > 0) {
                    availableQuantityRes += +item.availableQuantity;
                }
            }
            status.availableQuantity = availableQuantityRes;
            arr.push(status);
        }
        setSpecOneStatus(arr);
    };

    useEffect(() => {
        if (specs.length) {
            initSpecOneStatus();
        }
    }, [pageRawData]);

    useEffect(() => {
        // 秒杀商品部分sku库存为0
        if (pageRawData?.activityInfo?.activityId && showFlashActivity(pageRawData?.activityInfo?.status) && immediateAvailableQuantity === 0) {
            showToast({
                text: $k18n({ id: 'sku_0_activity_stock', text: '抱歉，该规格的活动库存为0' }),
                duration: 1000,
            });
            return;
        }

        if (immediateAvailableQuantity === 0) {
            if (!pageRawData?.extraInfo?.selfOperated) {
                showToast({
                    text: $k18n('抱歉，商品已售罄'),
                    duration: 1000,
                });
            }
            return;
        }
    }, [immediateAvailableQuantity]);

    // 通过点击图片规格 或者 文字规格 告诉父组件 点击的index
    const entranceType = useEntranceType();
    const watchSpecImageIndex = _specImageIndex => {
        let swiperImageIndex = _specImageIndex;
        if (_specImageIndex > -1) {
            swiperImageIndex = getStoreState().detailImageurls.length + _specImageIndex;
        }
        if (getStoreState().showVideo) {
            swiperImageIndex += 1;
        }
        if (!isNotAllImg) {
            if (_specImageIndex > -1) {
                setStoreState({
                    swiperImageIndex: swiperImageIndex,
                });
            } else {
                setStoreState({ swiperImageIndex: 0 });
            }
        }
        setStoreState({
            specImageIndex: _specImageIndex,
        });
    };
    const chooseSpec = (curentSpec: any, index: number, flag = true) => {
        logClickSpecs(pageRawData.itemId, entranceType);
        console.log('curentSpec', curentSpec, specs[0].itemSpecValues?.[index]);
        // isNotAllImg true 无规格图
        if (index === specImageIndex) {
            // setSelectedIndex(-1);
            watchSpecImageIndex(-1);
        } else {
            // setSelectedIndex(index);
            watchSpecImageIndex(index);
        }
        // 如果此时无库存
        // 重复触发设置库存
        if (!specOneStatus[index].availableQuantity && flag) {
            setStoreState({ immediateAvailableQuantity: 0 });
        } else {
            setStoreState({ immediateAvailableQuantity: specOneStatus?.[index]?.availableQuantity || 1 });
        }
    };

    const showDefaultCrossedPrice = pageRawData.marketPrice;

    // 如果不存在秒杀价格信息salePriceObj和分期价格信息installmentPrice，就使用这里的数据来展示价格信息。
    const fromPrice = (() => {
        // 如果有选中某规格
        if (specImageIndex > -1 && specOneStatus[specImageIndex]) {
            const targetPrice = specOneStatus[specImageIndex].salePrice;
            targetPrice.sort((a: { price: number }, b: { price: number }) => a.price - b.price);
            // 表明选中当前规格的所有sku (券后/标准)价格不都一样
            const showPriceMore = targetPrice[0]?.price !== targetPrice[targetPrice.length - 1]?.price;
            return {
                price: targetPrice[0]?.price,
                displayPrice: targetPrice[0]?.displayPrice,
                crossedPrice: targetPrice[0]?.marketPrice,
                node: isAfterUsingCoupon ? (
                    k18nPro({
                        text: '{after} {price}',
                        noRunK18n: true,
                        outerStyle: { fontSize: 12 },
                        params: {
                            after: <Text>{$k18n({ text: '券后', id: '1e202bd42f3b632dcf249fb70801e9e6' })}</Text>,
                            price: <Text style={{"fontSize":"4.830917874396135rem"}}>{targetPrice[0]?.displayPrice}</Text>,
                        },
                    })
                ) : showPriceMore ? (
                    k18nPro({
                        text: '{price}起',
                        id: 'k_269960',
                        outerStyle: { fontSize: 12 },
                        params: {
                            price: (
                                <Text style={{ fontSize: 20, color: showDefaultCrossedPrice ? '#ff4906' : 'rgba(0,0,0,0.88)' }}>
                                    {targetPrice[0]?.displayPrice}
                                </Text>
                            ),
                        },
                    })
                ) : (
                    <KText rtlClassName={['normal-price']}>{targetPrice[0]?.displayPrice}</KText>
                ),
                ifAllSame: !showPriceMore,
            };
        }
        // 如果没有选中某规格
        let showPriceMore = false;
        pageRawData?.skus?.forEach((sku: { salePrice: number }) => {
            if (sku && sku?.salePrice > pageRawData.salePrice) {
                showPriceMore = true;
            }
        });
        return {
            price: pageRawData.salePrice,
            displayPrice: pageRawData.displaySalePrice,
            crossedPrice: pageRawData.displayMarketPrice,
            node: isAfterUsingCoupon ? (
                k18nPro({
                    text: '{after} {price}',
                    noRunK18n: true,
                    outerStyle: { fontSize: 12 },
                    params: {
                        after: <Text>{$k18n({ text: '券后', id: '1e202bd42f3b632dcf249fb70801e9e6' })}</Text>,
                        price: <Text style={{"fontSize":"4.830917874396135rem"}}>{pageRawData.displaySalePrice}</Text>,
                    },
                })
            ) : showPriceMore ? (
                k18nPro({
                    text: '{price}起',
                    id: 'k_269960',
                    outerStyle: { fontSize: 12 },
                    params: {
                        price: <Text style={{"fontSize":"4.830917874396135rem"}}>{pageRawData.displaySalePrice}</Text>,
                    },
                })
            ) : (
                <KText rtlClassName={['normal-price']}>{pageRawData.displaySalePrice}</KText>
            ),
            ifAllSame: !showPriceMore,
        };
    })();

    const showTip = () => {
        Toast.show({
            text: $k18n({ text: '该商品在{APP_NAME}上的全球累计销量', params: { APP_NAME: isSnack ? 'SnackVideo' : 'Kwai' } }),
            duration: 1000,
            useModel: false,
        });
    };

    const renderSpecImages = () => {
        if (specs && specs[0] && specs[0].itemSpecValues) {
            return (
                <FlatList
                    showsHorizontalScrollIndicator={false}
                    style={{"paddingLeft":"4.347826086956522rem","paddingBottom":"2.8985507246376816rem","paddingTop":"3.8647342995169085rem","backgroundColor":"white"}}
                    data={specs[0].itemSpecValues}
                    horizontal={true}
                    keyExtractor={item => item.imageUrl}
                    renderItem={({ item, index }) => {
                        return (
                            <ViewVisible onVisible={logShowSpecs}>
                                <KTouchableWithoutFeedback key={'spec-area-img' + index} onPress={() => chooseSpec(item, index)}>
                                    <KView
                                        rtlClassName={[
                                            'img-wraper',
                                            index === specs[0].itemSpecValues.length - 1 ? 'spec-image-last' : '',
                                            specOneStatus[index] && specOneStatus[index].availableQuantity
                                                ? ''
                                                : pageRawData?.extraInfo?.selfOperated
                                                ? ''
                                                : 'img-wraper-no-quantity',
                                        ]}
                                        style={
                                            specImageIndex === index
                                                ? {
                                                      borderWidth: scaleW(1.5),
                                                      borderColor: '#ff4906',
                                                  }
                                                : {}
                                        }>
                                        <CropImage
                                            className={Styles['spec-img-item']}
                                            src={item?.templateImageUrl || item.imageUrl}
                                            cropOption={{ ratio: 0.3, width: 59, height: 59 }}
                                            width={40}
                                            height={40}
                                            logParams={{
                                                itemId,
                                                position: 'price-line',
                                            }}
                                        />
                                    </KView>
                                </KTouchableWithoutFeedback>
                            </ViewVisible>
                        );
                    }}
                />
            );
        } else {
            return null;
        }
    };

    /** 获取标签列表 */
    const tagList = pageRawData?.tagInfoAgg?.merchantTag?.filter(_item => _item.tagLabel === 9999).slice(0, 1) || [];
    /** 占位字符 */
    const getHiddenText = (tips?: string) => {
        if (!tips) {
            return '';
        }
        // 如果text是中文，占位符用英文字母代替，因为中文会换行
        // eslint-disable-next-line no-control-regex
        const text = tips?.replace(/[^\x00-\xff]/g, '00');
        return `${text}x`;
    };
    /** 标签列表组件 */
    const IconTextPriceLine = () => {
        return tagList.map(tag => (
            <KView
                rtlClassName={'pruduct-shippingIncluded'}
                style={{
                    backgroundColor: tag.tagCode === 'merchant_official' ? '#3A3A3A' : '#FF4906',
                }}>
                <KText
                    style={{
                        color: tag.tagCode === 'merchant_official' ? '#FFF0A1' : '#FFFFFF',
                    }}
                    rtlClassName={'pruduct-shippingIncluded-text'}>
                    {tag.tips || ''}
                </KText>
            </KView>
        ));
    };

    const { hasSubsidy } = useSubsidy();

    // 是否展示统一腰封
    const showBigBanner = getStoreState().bigMarketingBanner;
    //达人商品补贴需求： 不展示到手价，只展示划线价，没有划线价使用原价代替
    // 母亲节活动和达人补贴需求，价格展示一致 https://docs.corp.kuaishou.com/d/home/fcABll2-Lgy7mspBVx5T37iXO#section=vodka.uilg4qf2nt6f
    // 统一腰封价格区域展示逻辑跟达人商品展示一致，暂不修改变量名
    const isSubsidyGoods = hasSubsidy && pageRawData?.activityList?.find(v => v?.marketingActivityType === ActivityType.Exclusive);
    // 划线价逻辑调整：无划线价的时候不再使用原价替代划线价 https://team.corp.kuaishou.com/task/T7821488
    function getCrossPrice() {
        if (isEmpty(currentSku)) {
            return pageRawData.marketPrice ? pageRawData.displayMarketPrice : '';
        }
        return currentSku.skuMarketPrice ? currentSku.displaySkuMarketPrice : '';
    }

    const price = ({ showPrice = true }: { showPrice: boolean }) => {
        // 价格区域没有任何信息时直接展示空
        if (!pageRawData?.tagInfoAgg?.priceTag?.length && !showPrice && !getCrossPrice()) {
            return null;
        }
        return (
            <KView
                style={{"display":"flex","flexDirection":"row","justifyContent":"flex-start","alignItems":"center","flexWrap":"wrap","height":"5.555555555555556rem","overflow":"hidden"}}>
                {/* 价格卖点信息 */}
                {pageRawData?.tagInfoAgg?.priceTag?.length ? (
                    <View
                        style={{"height":"100%","alignItems":"center","justifyContent":"center"}}>
                        {pageRawData?.tagInfoAgg?.priceTag?.[0].tagCode === 'item_minPriceOfSimilar' ? (
                            <KView
                                rtlClassName={'product-price__discountdisplay-text-container'}
                                style={{"flexDirection":"row","alignItems":"center"}}>
                                <KImage
                                    source={{ uri: 'https://cdn-static.kwai.net/kos/s101/nlav11312/shop-krn/white_down.png' }}
                                    style={{"width":"2.8985507246376816rem","height":"4.347826086956522rem","marginRight":"0.48309178743961356rem"}}
                                />
                                <KText rtlClassName={['product-price__discountdisplay-text']}>{pageRawData?.tagInfoAgg?.priceTag?.[0]?.tips}</KText>
                            </KView>
                        ) : (
                            <KView
                                rtlClassName={'product-price__discountdisplay-text-container'}
                                style={{"flexDirection":"row","alignItems":"center","backgroundColor":"rgba(255, 73, 6, 0.1)"}}>
                                <KText
                                    rtlClassName={['product-price__discountdisplay-text']}
                                    style={{"color":"rgba(255, 73, 6, 1)","fontWeight":"400"}}>
                                    {pageRawData?.tagInfoAgg?.priceTag?.[0]?.tips}
                                </KText>
                            </KView>
                        )}
                    </View>
                ) : null}
                {/* 价格, 如果展示统一腰封，则不在这里透出价格*/}
                {showPrice ? <KText rtlClassName={'product-price__display-text'}>{fromPrice.node}</KText> : null}
                {/* 划线价 */}
                {/* 达人商品补贴○ 不展示到手价，只展示划线价，没有划线价使用原价代替 */}
                <KText rtlClassName={'product-price__marketprice-text'} style={{"textDecorationColor":"#999","alignSelf":"flex-end"}}>
                    {getCrossPrice()}
                </KText>
            </KView>
        );
    };

    const sellingPointComponent = (() => {
        const components = [];
        if (pageRawData.displayTotalSoldQuantity !== '0') {
            components.push(
                <View>
                    <KTouchableOpacity onPress={() => showTip()} style={styles.inventory}>
                        <KView style={{"flexDirection":"row","alignItems":"center"}}>
                            <KText style={{"fontSize":"2.8985507246376816rem","color":"rgba(153, 153, 153, 1)"}}>
                                {productSales(pageRawData.displayTotalSoldQuantity)}
                            </KText>
                            <KImage rtlClassName={'product-sales-img'} source={iconTips} />
                        </KView>
                    </KTouchableOpacity>
                </View>
            );
        }
        const itemTags = pageRawData?.tagInfoAgg?.itemTag || [];
        if (itemTags?.length) {
            itemTags.map(itemTag => {
                components.push(
                    <View>
                        <Text style={{"lineHeight":"3.8647342995169085rem","fontSize":"2.8985507246376816rem","color":"rgba(153, 153, 153, 1)"}}>{itemTag.tips}</Text>
                    </View>
                );
            });
        }
        const res = components.map((component, index) => {
            // 处理component, 给第二个及之后的组件加上间隔和竖线
            if (index > 0) {
                return (
                    <View style={{"flexDirection":"row","alignItems":"center"}}>
                        <View
                            style={{"height":"2.4154589371980677rem","width":"0.24154589371980678rem","backgroundColor":"rgba(0, 0, 0, 0.12)","marginHorizontal":"1.9323671497584543rem"}}
                        />
                        {component}
                    </View>
                );
            } else {
                return component;
            }
        });
        return res;
    })();
    return (
        <PageContext.Provider value={{ Styles }}>
            {specs && specs[0] && specs[0].itemSpecValues && !isNotAllImg && specs?.[0].itemSpecValues?.length > 1 ? renderSpecImages() : null}

            <KView
                rtlClassName={'pruduct-top'}
                style={{
                    paddingTop: isNotAllImg || specs?.[0].itemSpecValues?.length === 1 ? scaleW(16) : scaleW(4),
                    paddingBottom: pageRawData.sellerMerchantShop && pageRawData.sellerMerchantShop.merchantId !== 0 ? scaleW(8) : scaleW(16),
                }}>
                <View style={{"paddingHorizontal":"4.830917874396135rem"}}>
                    {price({
                        showPrice: !showBigBanner,
                    })}
                    <CouponEntryLineV2 />
                    <KTouchableOpacity
                        activeOpacity={1}
                        onPress={() => {
                            if (titleInfo.showOpenDetail) {
                                if (titleInfo.isFlat) {
                                    setTitleInfo({
                                        ...titleInfo,
                                        isFlat: false,
                                        numLines: 2,
                                    });
                                } else {
                                    setTitleInfo({
                                        ...titleInfo,
                                        numLines: 10,
                                        isFlat: true,
                                    });
                                }
                            }
                        }}>
                        <KView rtlClassName={'product-title-panel'}>
                            <KView rtlClassName={'product-title'}>
                                {/* 此处涉及逻辑较多，标签单独处理 */}
                                {IconTextPriceLine()}
                                <KText
                                    numberOfLines={titleInfo.numLines}
                                    ellipsizeMode={'tail'}
                                    onLayout={event => {
                                        const height = Math.floor(event.nativeEvent.layout.height || 0);
                                        // 第一次测量view的最大高度
                                        if (titleInfo.maxHeight === 0) {
                                            setTitleInfo({
                                                ...titleInfo,
                                                maxHeight: height,
                                                numLines: 2,
                                            });
                                            // 第二次当测量的最大高度>设置行数后的高度的时候，则需要展开按钮。
                                        } else if (titleInfo.maxHeight > height) {
                                            if (!titleInfo.showOpenDetail) {
                                                setTitleInfo({
                                                    ...titleInfo,
                                                    showOpenDetail: true,
                                                    isFlat: false,
                                                });
                                            }
                                        }
                                    }}
                                    style={{
                                        color: titleInfo.maxHeight === 0 ? 'rgba(0, 0, 0, 0)' : 'black',
                                    }}
                                    rtlClassName={'product-title-text'}>
                                    {tagList.map(tag => (
                                        <KText rtlClassName={'pruduct-shippingIncluded-text-hidden'}>
                                            {/* IOS 连续字符会不换行，遇到空格会换行，因为这个是占位文案，所以不希望它里面的空格换行，所以先把里面空格去掉 */}
                                            {/* bug team B1578043 */}
                                            {getHiddenText(tag.tips)}
                                        </KText>
                                    ))}
                                    {pageRawData.title}
                                </KText>
                            </KView>
                            {titleInfo.showOpenDetail ? (
                                <KImage
                                    style={[
                                        titleInfo.isFlat ? styles.up : {},
                                        {
                                            width: scaleW(16),
                                            height: scaleW(16),
                                        },
                                    ]}
                                    source={{
                                        uri: 'https://cdn-static.kwai.net/kos/s101/nlav11312/shop-krn/kibt_post_import_album_icon_arrow_black_16_bottom.png',
                                    }}
                                />
                            ) : null}
                        </KView>
                    </KTouchableOpacity>
                    {showFlash && showAbFlash && showFlashProgress ? <FlashProgress pageRawData={pageRawData} /> : null}
                    {/* 商品评分 + 库存提示 + 卖点 */}
                    {sellingPointComponent.length ? (
                        <View style={{"height":"3.8647342995169085rem","overflow":"hidden"}}>
                            <View style={{"flexWrap":"wrap","flexDirection":"row"}}>{sellingPointComponent}</View>
                        </View>
                    ) : null}
                </View>
                <ProductRank />
            </KView>

            {/* 分销外显 只是展示 无交互 */}
            {pageRawData.sellerMerchantShop && pageRawData.sellerMerchantShop.merchantId !== 0 ? (
                <KView rtlClassName={'talent-seller-area'} style={{"paddingBottom":"2.8985507246376816rem"}}>
                    <KImage
                        rtlClassName={'talent-seller-img'}
                        style={{"backgroundColor":"gray"}}
                        source={{ uri: pageRawData.sellerMerchantShop.imgUrl }}
                        cropOption={{ ratio: 0.5 }}
                    />
                    <KView rtlClassName={'talent-seller-name'}>
                        {/* 之前的逻辑判断是有问题的，必须确认该商品是补贴商品 */}
                        {isSubsidyGoods ? (
                            <>
                                <KText numberOfLines={2} ellipsizeMode={'tail'} rtlClassName={'talent-seller-name-text'}>
                                    {$k18n({
                                        id: 'userName_million',
                                        text: '来自{userName}的百万补贴',
                                        params: {
                                            userName: pageRawData.sellerMerchantShop.shopName || '店铺',
                                        },
                                    })}
                                </KText>
                            </>
                        ) : (
                            <KText numberOfLines={2} ellipsizeMode={'tail'} rtlClassName={'talent-seller-name-text'}>
                                {pageRawData.sellerMerchantShop.shopName}&nbsp;{$k18n({ id: 'k_332727', text: '推荐' })}
                            </KText>
                        )}
                    </KView>
                </KView>
            ) : null}

            {toastNode}
        </PageContext.Provider>
    );
}

export default PriceLine;
